import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { useLang } from "@/i18n";
import { API, WHATSAPP_NUMBER } from "@/lib/site";
import { Reveal, SectionHead } from "./Reveal";
import { ShineButton } from "./ShineButton";

const EMPTY = { name: "", email: "", phone: "", service: "", date: "", guests: "", message: "" };
const input = "w-full rounded-2xl border border-jungle/20 bg-paper px-4 py-3.5 text-[15px] text-jungle placeholder:text-jungle/45 transition-colors focus:border-mango focus:outline-none mt-2";
const label = "text-[11px] font-bold uppercase tracking-[0.16em] text-jungle/70";

const Field = ({ l, children, wide }) => (
  <label className={wide ? "sm:col-span-2" : ""}>
    <span className={label}>{l}</span>
    {children}
  </label>
);

export const BookingForm = () => {
  const { t, lang } = useLang();
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const h = (e) => setF((p) => ({ ...p, service: e.detail }));
    window.addEventListener("prefill-service", h);
    return () => window.removeEventListener("prefill-service", h);
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, { ...f, guests: f.guests ? Number(f.guests) : null, date: f.date || null, lang });
      toast.success(t.form.ok);
      setF(EMPTY);
    } catch {
      toast.error(t.form.err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contacto-section" className="relative bg-tropical-light py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHead id="contacto" eyebrow={t.form.eyebrow} title={t.form.title} />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base sm:text-lg text-jungle/80 max-w-md leading-relaxed">{t.form.intro}</p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t.whatsapp)}`}
              target="_blank" rel="noopener noreferrer" data-testid="contacto-whatsapp-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-white hover:scale-[1.03] transition-transform"
            >
              <MessageCircle size={16} /> WhatsApp +34 667 312 553
            </a>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} data-testid="booking-form" className="rounded-[2rem] bg-paper border border-jungle/10 p-6 sm:p-10 shadow-[0_40px_80px_-50px_rgba(8,26,19,0.6)] grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field l={`${t.form.name} *`}><input required minLength={2} value={f.name} onChange={set("name")} className={input} data-testid="booking-input-name" /></Field>
            <Field l={`${t.form.email} *`}><input required type="email" value={f.email} onChange={set("email")} className={input} data-testid="booking-input-email" /></Field>
            <Field l={`${t.form.phone} *`}><input required minLength={6} value={f.phone} onChange={set("phone")} placeholder="+34 ..." className={input} data-testid="booking-input-phone" /></Field>
            <Field l={`${t.form.service} *`}>
              <select required value={f.service} onChange={set("service")} className={input} data-testid="booking-input-service">
                <option value="" disabled>{t.form.choose}</option>
                {["cumpleanos", "fiestas", "reservas", "info"].map((s) => <option key={s} value={s}>{t.form.options[s]}</option>)}
              </select>
            </Field>
            <Field l={t.form.date}><input type="date" min={today} value={f.date} onChange={set("date")} className={input} data-testid="booking-input-date" /></Field>
            <Field l={t.form.guests}><input type="number" min={1} max={500} value={f.guests} onChange={set("guests")} className={input} data-testid="booking-input-guests" /></Field>
            <Field l={t.form.message} wide><textarea rows={4} value={f.message} onChange={set("message")} placeholder={t.form.messagePh} className={`${input} resize-none`} data-testid="booking-input-message" /></Field>
            <div className="sm:col-span-2">
              <ShineButton type="submit" disabled={sending} testId="booking-submit-button" className="w-full sm:w-auto !px-8">
                {sending ? t.form.sending : t.form.submit}
              </ShineButton>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
